import React, { useEffect, useState } from "react";
import { Check, Loader2, RefreshCw } from "lucide-react";

import {
  getVisionAnalysisPlan,
  runFullReferenceVisionOrchestration,
} from "../../lib/brandStudioApi";

function errorText(error) {
  const detail = error?.response?.data?.detail;
  const code =
    typeof detail === "object" && detail
      ? detail.code
      : typeof detail === "string"
        ? detail
        : "";
  return code || "Не удалось выполнить full-reference Vision orchestration.";
}

export default function VisionOrchestrationOwnerPanelV1() {
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadPlan = async () => {
    setStatus("loading");
    setError("");
    try {
      setPlan(await getVisionAnalysisPlan());
      setStatus("ready");
    } catch (requestError) {
      setStatus("error");
      setError(errorText(requestError));
    }
  };

  useEffect(() => {
    loadPlan();
  }, []);

  const run = async () => {
    if (busy || !plan || plan.needs_analysis_count === 0) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const next = await runFullReferenceVisionOrchestration();
      setResult(next);
      await loadPlan();
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="vision-orchestration-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        OWNER QA · VU4.6
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Все референсы участвуют в Visual Understanding
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Сначала проверяю immutable Vision Cache. Уже проанализированные изображения
        повторно в Mistral не отправляются. Вызовы будут сделаны только для недостающих
        активных референсов. Synthesis на этом шаге не запускается.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#806B55]">
          <Loader2 size={14} className="animate-spin" /> Проверяю cache…
        </div>
      )}

      {plan && (
        <div className="mt-5 grid gap-2 sm:grid-cols-4">
          <div className="rounded-[13px] bg-white px-4 py-3 text-[11px] text-[#655141]">
            Всего refs: <strong>{plan.total_references}</strong>
          </div>
          <div className="rounded-[13px] bg-white px-4 py-3 text-[11px] text-[#655141]">
            Уже cached: <strong>{plan.cached_count}</strong>
          </div>
          <div className="rounded-[13px] bg-white px-4 py-3 text-[11px] text-[#655141]">
            Пойдут в Mistral: <strong>{plan.needs_analysis_count}</strong>
          </div>
          <div className="rounded-[13px] bg-white px-4 py-3 text-[11px] text-[#655141]">
            Недоступны: <strong>{plan.unavailable_count}</strong>
          </div>
        </div>
      )}

      {plan?.unavailable_count > 0 && (
        <div className="mt-4 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[11px] leading-5 text-[#6B443A]">
          Есть недоступные исходные файлы. Полный visual universe нельзя считать готовым,
          пока они не будут восстановлены или удалены пользователем.
        </div>
      )}

      {plan?.needs_analysis_count > 0 && (
        <button
          type="button"
          disabled={busy}
          onClick={run}
          className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-[12px] text-[#FFF8ED] disabled:opacity-50"
          data-testid="vision-orchestration-run-v1"
        >
          {busy ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Анализирую недостающие референсы…
            </>
          ) : (
            <>
              <RefreshCw size={14} />
              Проанализировать {plan.needs_analysis_count} недостающих
            </>
          )}
        </button>
      )}

      {plan?.needs_analysis_count === 0 && plan?.unavailable_count === 0 && (
        <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#EEF0DF] px-4 py-2 text-[11px] text-[#586044]">
          <Check size={13} /> Все {plan.total_references} референсов уже в Vision Cache
        </div>
      )}

      {error && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}

      {result && (
        <div
          className="mt-6 rounded-[14px] border border-[#DCCDB8] bg-white p-4"
          data-testid="vision-orchestration-result-v1"
        >
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#927A5B]">
            Результат
          </div>
          <div className="mt-2 text-[12px] leading-6 text-[#4C392B]">
            Было cached: {result.cached_before} · проанализировано сейчас: {result.analyzed_now}
            {" · "}cached после: {result.cached_after} из {result.total_references}.
          </div>
          <div className="mt-1 text-[11px] leading-5 text-[#806B55]">
            Ошибок: {result.failed?.length || 0} · недоступно: {result.unavailable_count}
            {" · "}осталось: {result.remaining_count}.
          </div>
          {result.complete && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} /> Полный visual universe готов к VU4 synthesis
            </div>
          )}
        </div>
      )}
    </section>
  );
}
